import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useAlert } from '../../context/AlertContext';
import {
  useCreateRatingForm,
  useGetRatingFormFullObject,
  useUpdateRatingForm,
} from '../../hooks/reactQuerys/RatingFormQueries';
import { IRatingForm } from '../../Interfaces/IRatingForm';
import {
  RatingFormStructureEditor,
  newRatingQuestion,
} from './RatingFormStructureEditor';

interface IProps {
  form: IRatingForm | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const emptyDraft = (): IRatingForm => ({
  Title: '',
  IsDefault: false,
  IsActive: true,
  ThresholdScore: 4,
  ThanksText:
    'Vielen Dank für Ihre positive Rückmeldung. Wenn Sie möchten, hinterlassen Sie uns gerne eine öffentliche Bewertung.',
  ThanksLinkLabel: 'Auf Google bewerten',
  ThanksLinkUrl: '',
  Questions: [newRatingQuestion()],
  ConfirmationEnabled: false,
});

export const RatingFormEditDialog: React.FC<IProps> = ({
  form,
  open,
  onClose,
  onSaved,
}) => {
  const { showAlert } = useAlert();
  const isEdit = !!form?.idRatingForm;
  const { ratingForm, isLoadingRatingForm } = useGetRatingFormFullObject(
    isEdit ? form!.idRatingForm! : null
  );
  const { createRatingForm, isPendingCreate } = useCreateRatingForm();
  const { updateRatingForm, isPendingUpdate } = useUpdateRatingForm();

  const [draft, setDraft] = useState<IRatingForm>(emptyDraft());

  useEffect(() => {
    if (isEdit && ratingForm) {
      setDraft({
        ...ratingForm,
        IsDefault: !!ratingForm.IsDefault,
        IsActive: ratingForm.IsActive !== false,
        // MariaDB liefert TINYINT als 0/1, Connexion verlangt echtes
        // boolean — explizit casten, sonst 400 beim Speichern.
        ConfirmationEnabled: !!ratingForm.ConfirmationEnabled,
        Questions: (ratingForm.Questions ?? []).map(q => ({
          ...q,
          IsRequired: q.IsRequired !== false,
          CountsForOverall: q.CountsForOverall !== false,
        })),
      });
    } else if (!isEdit) {
      setDraft(emptyDraft());
    }
  }, [isEdit, ratingForm]);

  const isSaving = isPendingCreate || isPendingUpdate;

  const handleSave = async () => {
    if (!draft.Title.trim()) {
      showAlert({ text: 'Titel ist erforderlich.', severity: 'warning' });
      return;
    }
    const questions = (draft.Questions ?? [])
      .map((q, idx) => ({
        ...q,
        OrderNo: (idx + 1) * 10,
        Label: (q.Label || '').trim(),
      }))
      .filter(q => q.Label !== '');

    if (questions.length === 0) {
      showAlert({
        text: 'Mindestens eine Frage mit Bezeichnung wird benötigt.',
        severity: 'warning',
      });
      return;
    }

    const payload: IRatingForm = {
      ...draft,
      Title: draft.Title.trim(),
      Questions: questions,
    };

    try {
      if (isEdit) {
        await updateRatingForm({
          ...payload,
          idRatingForm: form!.idRatingForm,
        });
      } else {
        await createRatingForm(payload);
      }
      showAlert({ text: 'Bewertungsbogen gespeichert.', severity: 'success' });
      await onSaved();
    } catch (err) {
      console.error(err);
      showAlert({ text: 'Speichern fehlgeschlagen.', severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        {isEdit ? 'Bewertungsbogen bearbeiten' : 'Neuer Bewertungsbogen'}
      </DialogTitle>
      <DialogContent dividers>
        {isEdit && isLoadingRatingForm ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <RatingFormStructureEditor value={draft} onChange={setDraft} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <Stack direction='row' alignItems='center' spacing={1}>
              <span>Speichern</span>
              <CircularProgress size={18} />
            </Stack>
          ) : (
            'Speichern'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
